<template>
  <div class="type-step-container">
    Alert me when ...

    <v-container p-0 m-0 class="pa-0">
      <v-row dense row wrap>
        <v-col class="col-6">
          <v-container class="pa-0">
            <v-row dense class="mt-3" row wrap>
              <v-col class="col-12">
                <HotspotsSelector
                  v-model="alert.hotspotsEnabled"
                  @input="handleHotspotsChange"
                ></HotspotsSelector>
              </v-col>
              <v-col class="col-12 mb-5">
                <PulseSelector
                  v-model="alert.pulseEnabled"
                  :details="alert.pulseDetails"
                  :disabled="!isPulseAvailable"
                  :processing="pulseLowerBoundLoading"
                  :errorMessage="pulseLowerBoundError"
                  @input="handlePulseChange"
                  @change="handlePulseBoundChange"
                ></PulseSelector>
              </v-col>
              <v-col class="col-12 mb-5">
                <EventsSelector
                  ref="eventsSelector"
                  :events-selection="alert.eventsSelection"
                  :disabled="hasEssentials"
                  @changed="handleEventsChange"
                  @toggled-on="handleEventsEnable"
                ></EventsSelector>
              </v-col>
              <v-col class="col-12">
                <VerifiedEventsSelector
                  ref="verifiedEventsSelector"
                  :selection="alert.verifiedEventsSelection"
                  @changed="handleVerifiedEventsChange"
                  @toggled-on="handleVerifiedEventsEnable"
                ></VerifiedEventsSelector>
              </v-col>
            </v-row>
          </v-container>
        </v-col>
        <v-col dense class="text-center col-6">
          <TripWiresDisplay :alert="alert"></TripWiresDisplay>
          <AlertDiscontinuedInfo></AlertDiscontinuedInfo>
        </v-col>
      </v-row>
    </v-container>

    <v-divider class="my-4"></v-divider>

    <v-switch
      :value="advancedOpen"
      :disabled="hasEssentials"
      label="Advanced"
      @change="toggleAdvanced"
      solo
      class="ml-2"
      :class="{ 'name-label-disabled': hasEssentials }"
    ></v-switch>

    <v-container v-if="advancedOpen" class="advanced-container pt-0">
      <div class="mb-3">
        <span class="application-heading"
          >Use these advanced features to build a custom alert just for
          you!</span
        >
      </div>
      <v-container class="keyword-container">
        <KeywordEditor
          v-model="alert.keywords"
          :keyword-limit="keywordLimit"
        ></KeywordEditor>
      </v-container>

      <v-container class="source-container">
        Select your sources
        <SourcesEditor
          v-model="alert.sources"
          @input="handleSourceChange"
        ></SourcesEditor>
      </v-container>
    </v-container>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashFind from "lodash/find";
import lodashCloneDeep from "lodash/cloneDeep";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import { getCountryByCode } from "@/common/maps/utilities";
import {
  validateTripwires,
  validateKeywords,
} from "@/services/alerts/alert.validation.js";
import {
  getSources,
  getDefaultSources,
} from "@/services/alerts/alerts.service.js";
import { getPrimaryTopics } from "@/services/events/topics.service";
import { KEYWORD_MAX_LIMIT_EXT } from "@/common/constants/alert.js";
import {
  hasTwitterAccess,
  hasEssentialsAccess,
} from "@/services/user/user.service";
import { getStabilityByCountry } from "@/services/grids/stability.service";

const EMITTED_ACTIONS = {
  STEP_FINISHED: "stepFinished",
  PROCESSING: "processingChange",
};

let mostRecentDetailsRequestTime = dayjs();

export default {
  name: "TripWiresStep",
  components: {
    SourcesEditor: () =>
      import("@/components/alerts/wizard/sources/SourcesEditor"),
    KeywordEditor: () =>
      import("@/components/alerts/wizard/keywords/KeywordEditor"),
    HotspotsSelector: () =>
      import("@/components/alerts/wizard/hotspots/HotspotsSelector"),
    PulseSelector: () =>
      import("@/components/alerts/wizard/pulse/PulseSelector"),
    EventsSelector: () =>
      import("@/components/alerts/wizard/events/EventsSelector"),
    VerifiedEventsSelector: () =>
      import(
        "@/components/alerts/wizard/verifiedEvents/VerifiedEventsSelector"
      ),
    TripWiresDisplay: () =>
      import("@/components/alerts/wizard/steps/TripWiresDisplay"),
    AlertDiscontinuedInfo: () =>
      import("@/components/common/details/AlertDiscontinuedInfo"),
  },
  props: {
    alert: {
      type: Object,
      required: true,
    },
    isActive: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      advancedOpen: false,
      pulseLowerBoundError: null,
      pulseLowerBoundLoading: false,
      hasWebGL: this.isAvailableWebGL(),
      hasEssentials: hasEssentialsAccess(),
    };
  },
  computed: {
    isPulseAvailable() {
      // if not country selected then country pulse drop needs to be disabled
      return !!(
        !this.hasEssentials &&
        lodashGet(this.alert, "aoiFeature.properties.aoiIds") &&
        Array.isArray(this.alert.aoiFeature.properties.aoiIds) &&
        this.alert.aoiFeature.properties.aoiIds.length &&
        this.alert.aoiFeature.properties.aoiIds[0].length === 2
      );
    },
    keywordLimit() {
      return KEYWORD_MAX_LIMIT_EXT;
    },
  },
  mounted() {
    if (validateKeywords(this.alert)) {
      this.advancedOpen = true;
    }
    if (this.alert.pulseEnabled) {
      this.pulseLowerBoundError = null;
      this.buildPulseDetails();
    } else {
      this.resetPulseEnabled();
    }
  },
  methods: {
    validate() {
      const isValid = validateTripwires(this.alert);
      this.$emit(EMITTED_ACTIONS.STEP_FINISHED, { isValid });
    },
    toggleAdvanced() {
      this.advancedOpen = !this.advancedOpen;
      if (this.advancedOpen) {
        this.unselect();
        this.resetVerifiedEvents();
      }
    },
    unselect() {
      this.resetHotspotsEnabled();
      this.resetPulseEnabled();
      this.resetEvents();
    },
    handleHotspotsChange() {
      this.resetPulseEnabled();
      this.resetKeywords();
      this.resetSources();
    },
    handlePulseChange() {
      this.resetHotspotsEnabled();
      this.resetKeywords();
      this.resetSources();
      this.resetEvents();
      this.advancedOpen = false;

      if (this.alert.pulseEnabled) {
        this.pulseLowerBoundError = null;
        this.buildPulseDetails();
      } else {
        this.resetPulseEnabled();
      }
    },
    handlePulseBoundChange(index, value, useLowestDynamicBound) {
      this.pulseLowerBoundError = "";
      if (value) {
        if (parseInt(value) < 1 || parseInt(value) > 100) {
          this.pulseLowerBoundError = "Range must be a number from 1 to 100";
        }
        const details = lodashCloneDeep(this.alert.pulseDetails[index]);
        details.lowerBound = value;
        details.useLowestDynamicBound = useLowestDynamicBound;

        this.$set(this.alert.pulseDetails, index, details);

        this.validate();
      }
    },
    handleEventsChange(item) {
      this.resetPulseEnabled();
      this.resetKeywords();
      this.resetSources();
      const eventTypeInfo = {
        useMainTopic: item.useMainTopic,
        categories: item.categories,
      };
      this.$set(this.alert.eventsSelection, item.type, eventTypeInfo);
    },
    handleEventsEnable() {
      this.resetPulseEnabled();
      this.resetVerifiedEvents();
      this.resetKeywords();
      this.resetSources();
    },
    handleVerifiedEventsEnable() {
      this.unselect();
      this.resetKeywords();
      this.resetSources();
      this.advancedOpen = false;
    },
    handleVerifiedEventsChange(item) {
      this.unselect();
      this.resetKeywords();
      this.resetSources();
      this.$set(this.alert.verifiedEventsSelection, "type", item.type);
      this.$set(
        this.alert.verifiedEventsSelection,
        "categories",
        item.categories
      );
      this.$set(this.alert, "sources", null);
    },
    handleSourceChange() {
      this.unselect();
    },
    resetHotspotsEnabled() {
      this.$set(this.alert, "hotspotsEnabled", false);
    },
    resetPulseEnabled() {
      this.$set(this.alert, "pulseEnabled", false);
      delete this.alert.pulseDetails;
      this.pulseLowerBoundError = null;
    },
    resetKeywords() {
      this.$set(this.alert, "keywords", {
        includes: [{ values: [] }],
        excludes: [],
      });
    },
    resetSources() {
      this.$set(this.alert, "sources", getDefaultSources());
    },
    resetEvents() {
      let eventsSelection = {};
      const PRIMARY_TOPICS = getPrimaryTopics();
      PRIMARY_TOPICS.forEach((topic) => {
        eventsSelection[topic.type] = null;
      });
      this.$set(this.alert, "eventsSelection", eventsSelection);
      this.$refs.eventsSelector.disable();
    },
    resetVerifiedEvents() {
      let verifiedEventsSelection = {
        type: null,
        categories: [],
      };
      this.$set(this.alert, "verifiedEventsSelection", verifiedEventsSelection);
      if (lodashGet(this.$refs, "verifiedEventsSelector")) {
        this.$refs.verifiedEventsSelector.disable();
      }
    },
    async buildPulseDetails() {
      let startTime = dayjs();
      this.pulseLowerBoundLoading = true;
      try {
        let aoiIds = lodashGet(this.alert, "aoiFeature.properties.aoiIds");
        let locations = [];
        for (let i = 0; i < aoiIds.length; i++) {
          let userLowerBound;
          let useLowestDynamicBound = true;

          if (
            this.alert.pulseDetails &&
            Array.isArray(this.alert.pulseDetails) &&
            this.alert.pulseDetails[i]
          ) {
            if (this.alert.pulseDetails[i].lowerBound) {
              userLowerBound = Math.floor(
                this.alert.pulseDetails[i].lowerBound
              );
            }
            if (
              this.alert.pulseDetails[i].hasOwnProperty("useLowestDynamicBound")
            ) {
              useLowestDynamicBound = this.alert.pulseDetails[i]
                .useLowestDynamicBound;
            } else {
              if (this.alert.id) {
                useLowestDynamicBound = false;
              }
            }
          }
          locations.push({
            aoiId: aoiIds[i],
            userLowerBound: userLowerBound,
            useLowestDynamicBound: useLowestDynamicBound,
          });
        }

        //NOTE: The order of this array must match the order of the location array
        let details = [];
        if (
          this.hasWebGL &&
          lodashGet(this.alert, `aoiFeature.properties.aoiProperties`)
        ) {
          //NOTE: When editing(adding another country) an existing pulse alert after a page refresh existing
          // countries are not a part of the alert.aoiFeature.properties.aoiProperties object. This array
          // will be used to collect the Ids then call the API populate those missing fields.
          const missingAoiProps = [];
          for (let i = 0; i < locations.length; i++) {
            const locationDetails = locations[i];
            const aoiProps = lodashGet(
              this.alert,
              `aoiFeature.properties.aoiProperties[${locationDetails.aoiId}]`
            );
            if (!aoiProps) {
              missingAoiProps.push(locationDetails.aoiId);
              continue;
            }
            const location = await this.buildPulseDetailsForAoiUsingTiles(
              aoiProps,
              locationDetails.aoiId,
              locationDetails.userLowerBound,
              locationDetails.useLowestDynamicBound
            );
            details.push(location);
          }

          if (missingAoiProps.length > 0) {
            const missingPropDetails = await this.buildPulseDetailsForAoiUsingApi(
              missingAoiProps,
              locations
            );
            details.push(...missingPropDetails);
          }
        } else {
          details = await this.buildPulseDetailsForAoiUsingApi(
            aoiIds,
            locations
          );
        }
        if (startTime.isAfter(mostRecentDetailsRequestTime)) {
          this.$set(this.alert, "pulseDetails", details);
          mostRecentDetailsRequestTime = startTime;
          this.pulseLowerBoundLoading = false;
        }
      } catch (err) {
        console.error("Failed getting pulse lower bound");
        console.error(err);
        this.pulseLowerBoundError =
          "Failed retrieve values necessary for pulse alert. Please try again.";
        this.$set(this.alert, "pulseEnabled", false);
        delete this.alert.pulseDetails;
        this.pulseLowerBoundLoading = false;
      }
    },
    async buildPulseDetailsForAoiUsingTiles(
      aoiProps,
      aoiId,
      userLowerBound,
      useLowestDynamicBound
    ) {
      if (!aoiProps) {
        throw new Error("NO AOI PROPS");
      }
      let lowerBound = aoiProps.pvl;
      let currentArr = aoiProps.wh;
      currentArr = JSON.parse(currentArr);
      let current = currentArr[currentArr.length - 1];
      let min = aoiProps.mi;
      let max = aoiProps.ma;
      let avgLow = aoiProps.pvl;
      let avgHigh = aoiProps.pvh;

      return await this.setPulseDetailsForCountry({
        aoiId: aoiId,
        useLowestDynamicBound: useLowestDynamicBound,
        lowerBound: userLowerBound || lowerBound,
        lowerBoundOriginal: lowerBound,
        current: current,
        min: min,
        max: max,
        avgLow: avgLow,
        avgHigh: avgHigh,
      });
    },
    async buildPulseDetailsForAoiUsingApi(aoiIds, locations) {
      let res = await getStabilityByCountry({
        start: dayjs.utc().startOf("day").toISOString(),
        end: dayjs.utc().endOf("day").toISOString(),
        local: true,
        aoi: aoiIds.join(),
      });

      let details = [];

      if (
        lodashGet(res, "features") &&
        Array.isArray(res.features) &&
        res.features.length
      ) {
        for (let i = 0; i < res.features.length; i++) {
          const apiFeature = res.features[i];
          const aoiId = lodashGet(apiFeature, "properties.country");

          const lowerBound = lodashGet(
            apiFeature,
            "properties.stability_score.stats.percentiles.very_low"
          );
          const current = lodashGet(
            apiFeature,
            "properties.stability_score.count"
          );
          const min = lodashGet(
            apiFeature,
            "properties.stability_score.stats.min"
          );
          const max = lodashGet(
            apiFeature,
            "properties.stability_score.stats.max"
          );
          const avgLow = lodashGet(
            apiFeature,
            "properties.stability_score.stats.percentiles.very_low"
          );
          const avgHigh = lodashGet(
            apiFeature,
            "properties.stability_score.stats.percentiles.very_high"
          );

          const location = lodashFind(locations, (loc) => {
            return loc.aoiId.toUpperCase() === aoiId.toUpperCase();
          });

          const result = await this.setPulseDetailsForCountry({
            aoiId: aoiId,
            useLowestDynamicBound: location.useLowestDynamicBound,
            lowerBound: location.userLowerBound || lowerBound,
            lowerBoundOriginal: lowerBound,
            current: current,
            min: min,
            max: max,
            avgLow: avgLow,
            avgHigh: avgHigh,
          });

          details.push(result);
        }
      }
      return details;
    },
    async setPulseDetailsForCountry(props) {
      if (!props.aoiId) {
        throw "Failed to get pulse details: No aoiId";
      }
      if (!props.lowerBound) {
        throw "Failed to get pulse details: No lower bound";
      }
      const lowerBound = Math.floor(props.lowerBound);

      const range =
        props.avgLow && props.avgHigh
          ? `${Math.floor(props.avgLow)}-${Math.floor(props.avgHigh)}`
          : null;

      const current = props.current ? Math.floor(props.current) : null;

      let result = await getCountryByCode(props.aoiId);
      result = JSON.parse(result);
      let countryName = props.aoiId;
      if (lodashGet(result, "data.properties.displayName")) {
        countryName = result.data.properties.displayName;
      }

      return {
        id: props.aoiId,
        name: countryName,
        type: "country",
        useLowestDynamicBound: props.useLowestDynamicBound,
        lowerBound: lowerBound,
        lowerBoundOriginal: props.lowerBoundOriginal,
        current: current,
        min: props.min,
        max: props.max,
        range: range,
      };
    },
  },
  watch: {
    isActive() {
      if (!this.isActive) {
        return;
      }
      if (this.alert.sources && this.alert.sources.length === 0) {
        let sources = [];
        const SOURCES = getSources();
        const NEWS = lodashFind(SOURCES, ["id", "news-all"]);
        sources.push(NEWS);
        if (hasTwitterAccess()) {
          const SOCIAL = lodashFind(SOURCES, ["id", "twitter"]);
          sources.push(SOCIAL);
        }
        this.$set(this.alert, "sources", sources);
      }
      if (this.alert.pulseEnabled && !this.isPulseAvailable) {
        this.alert.pulseEnabled = false;
      }
    },
    ["alert.sources"]() {
      this.validate();
    },
    "alert.keywords": {
      handler() {
        this.validate();
        if (validateKeywords(this.alert)) {
          this.unselect();
          this.resetVerifiedEvents();
          this.advancedOpen = true;
        }
      },
      deep: true,
    },
    "alert.hotspotsEnabled": {
      handler(val) {
        if (val) {
          this.resetVerifiedEvents();
        }
        this.validate();
      },
      deep: true,
    },
    "alert.pulseEnabled": {
      handler(val) {
        if (val) {
          this.resetVerifiedEvents();
        }
        this.$set(this.alert, "pulseEnabled", val);
        this.validate();
      },
      deep: true,
    },
    "alert.eventsSelection": {
      handler(topics) {
        let hasVal = false;
        for (let i = 0; i < topics.length; i++) {
          if (topics[i]) {
            hasVal = true;
            break;
          }
        }
        if (hasVal) {
          this.resetVerifiedEvents();
        }
        this.validate();
      },
      deep: true,
    },
    "alert.aoiFeature.properties.aoiIds": {
      handler(value) {
        if (!this.alert.pulseEnabled) {
          return;
        }
        if (!value || value.length === 0) {
          this.validate();
          return;
        }
        value = JSON.parse(JSON.stringify(value));

        const alertAois = value.sort();
        if (
          this.alert &&
          this.alert.pulseDetails &&
          this.alert.pulseDetails.length > 0
        ) {
          const pulseDetailAois = this.alert.pulseDetails
            .map((item) => {
              return item.id;
            })
            .sort();
          if (
            JSON.stringify(alertAois) === JSON.stringify(pulseDetailAois) &&
            !this.pulseLowerBoundLoading
          ) {
            this.validate();
            return;
          }
        }

        this.buildPulseDetails();
      },
    },
    pulseLowerBoundLoading(val) {
      this.$emit(EMITTED_ACTIONS.PROCESSING, { processing: val });
    },
  },
};
</script>

<style scoped>
.keyword-container {
  padding: 0;
}
.source-container {
  padding: 15px 0 0 0;
}
.type-step-container {
  height: 65vh;
  overflow-y: auto;
  overflow-x: hidden;
}
.name-label-disabled .v-label {
  color: #c7c7c7 !important;
}
</style>
